import { TODO } from '@b2b/types/core/utils';
import angular from 'angular';

export class NavigationService {
  static $inject = ['AuthService'];
  isShown = true;
  securityEnhanced = false;
  currentStateName = '';
  foundStateName = '';
  security: TODO = {};
  items: TODO = {};
  current: TODO[] = [];

  constructor(private AuthService: TODO) { }

  /**
   * Optionally initialize entire navigation
   */
  init(items: TODO, isSecure: boolean, engine: 'permissions' | null): boolean {
    if (items) {
      this.items = items;
    }

    this.securityEnhanced = isSecure === true;
    if (this.securityEnhanced) {
      this.security.features = this.AuthService.getFeatures();

      if (engine === 'permissions') {
        this.security.engine = engine;
        this.security.permissions = this.AuthService.getPermissions();
        this.filterByEngine();
      } else {
        this.security.engine = 'roles';
        this.security.roles = this.AuthService.getRoles();
        this.filterByEngine();
      }
    }

    return true;
  }

  reset() {
    this.securityEnhanced = false;
    this.security = {};
    this.items = {};
    this.current = [];
  }

  toggle() {
    return (this.isShown = !this.isShown);
  }

  show() {
    return (this.isShown = true);
  }

  hide() {
    return (this.isShown = false);
  }

  /**
   * Adds an item to navigation
   *
   * Accepts:
   * {
   *   name: {String} // Used as key in hash
   *   title: {String}
   *   sref: {String} // $state named view Optional
   *   href: {String} // Optional
   *   roles: {String} // Optional admin, user, batch_user
   *   items: {Array} // Optional contains same properties as parent object
   * }
   */
  add(item: TODO, stateName: string): boolean {
    let r = false;
    const allow = this.checkSecurity(item);
    const state = this.findState(stateName);

    if (item && item.hasOwnProperty('name') && allow) {
      const _name = item.name;

      if (!state) {
        this.items[stateName] = [];
      }

      if (!this.find(_name, stateName)) {
        this.items[stateName].push(item);
      }

      r = true;
    }

    return r;
  }

  updateItem(updatedItem, stateName) {
    const itemIndex = this.find(updatedItem.name, stateName, true);

    if (itemIndex !== false) {
      const currentItem = this.items[stateName][itemIndex];

      // Add nested routes
      updatedItem.nested.forEach(function (route) {
        currentItem.nested.push(route);
      });

      // Add permissions
      updatedItem.permissions.forEach(function (permission) {
        currentItem.permissions.push(permission);
      });

      // Add items
      updatedItem.items.forEach(function (item) {
        currentItem.items.push(item);
      });

      this.filterByEngine();
    }
  }

  /**
   * Removes an item from navigation
   */
  remove(name: string, stateName: string) {
    if (this.items.hasOwnProperty(stateName)) {
      for (let i = 0, ii = this.items[stateName].length; i < ii; i++) {
        if (this.items[stateName][i].name === name) {
          this.items[stateName].splice(i, 1);
          break;
        }
      }
    }
  }

  /**
   * Find a particular entry does not traverse the entire tree.
   * Only top level items are retrievable
   */
  find(name: string, stateName: string, returnIndexOnly?: boolean, searchField?: TODO) {
    let result: number | false = false;
    const field = searchField ? searchField : 'name';

    if (this.items.hasOwnProperty(stateName)) {
      for (let i = 0, ii = this.items[stateName].length; i < ii; i++) {
        if (this.items[stateName][i][field] === name) {
          if (returnIndexOnly) {
            result = i;
          } else {
            result = this.items[stateName][i];
          }
          break;
        }
      }
    }

    return result;
  }

  deepFind(name, stateName, searchField): TODO {
    let result = false;
    const field = searchField ? searchField : 'name';

    if (this.items.hasOwnProperty(stateName)) {
      for (let i = 0, ii = this.items[stateName].length; i < ii; i++) {
        if (this.items[stateName][i][field] === name) {
          result = this.items[stateName][i];
          break;
        } else if (this.items[stateName][i].hasOwnProperty('items')) {
          for (let j = 0, jj = this.items[stateName][i].items.length; j < jj; j++) {
            if (this.items[stateName][i].items[j][field] === name) {
              result = this.items[stateName][i];
              break;
            }
          }
        }
      }
    }

    return result;
  }

  /**
   * Finds an entire state navigation
   */
  findState(stateName?: string): TODO {
    let result = false;

    if (stateName) {
      // Quick match
      if (this.items.hasOwnProperty(stateName)) {
        result = this.items[stateName];
        this.foundStateName = stateName;
      } else {
        // Search for any states downstream
        for (const item in this.items) {
          if (this.items.hasOwnProperty(item)) {
            const regex = new RegExp('^' + item + '.*');

            if (regex.test(stateName)) {
              result = this.items[item];
              this.foundStateName = item;
              break;
            }
          }
        }
      }
    }

    return result;
  }

  toggleState(stateName, currentStateName) {
    if (this.items.hasOwnProperty(currentStateName)) {
      for (let i = 0, ii = this.items[currentStateName].length; i < ii; i++) {
        this.items[currentStateName][i].toggle = false;
      }
    }

    try {
      this.deepFind(stateName, currentStateName, 'sref').toggle = true;
    } catch (e) {
      // State wasn't found
    }
  }

  /**
   * Removes one item for another in navigation
   * This will remove all subtree items as well
   */
  swap(name: string, stateName: string, item: TODO): boolean {
    const i = this.find(name, stateName, true);
    let r = false;
    let result = [];

    if (i !== false) {
      result = this.items[stateName].splice(i, 1, item);
      if (result.length === 1) {
        r = true;
      }
    }
    return r;
  }

  /**
   * Updates the currently used state
   */
  update(stateName?: string) {
    let r = false;
    const state = this.findState(stateName);

    if (state) {
      this.currentStateName = this.foundStateName;
      this.current = state;
      r = true;
    } else {
      this.currentStateName = 'default';
      this.current = this.findState('default');
    }

    this.toggleState(stateName, this.currentStateName);

    return r;
  }

  /**
   * Validate roles are allowed
   * Accepts a navigation Object
   */
  checkSecurity(item: { roles: TODO[]; permissions: TODO[] }): boolean {
    let allow = false;

    if (this.security.engine === 'roles') {
      allow = this.checkRoles(item.roles);
    } else {
      allow = this.checkPermissions(item.permissions);
    }

    return allow;
  }

  /**
   * Validate roles are allowed
   * Accepts an Array of Strings or a String
   */
  checkRoles(roles: string | string[]): boolean {
    let _roles: TODO[] = [];
    let allow = false;

    if (this.securityEnhanced) {
      // Verify roles are accepted by cookie
      if (Array.isArray(roles)) {
        _roles = roles;
      } else {
        _roles = ['user'];
      }

      for (let i = 0, ii = _roles.length; i < ii; i++) {
        if (this.security.roles.indexOf(_roles[i]) !== -1) {
          allow = true;
          break;
        }
      }
    } else {
      // Allow anything that is not security enhanced
      allow = true;
    }

    return allow;
  }

  /**
   * Validate permissions are allowed
   * Accepts an Array of Strings or a String
   */
  checkPermissions(permissions: TODO): boolean {
    let _permissions: TODO[] = [];
    let allow = false;

    if (this.securityEnhanced) {
      // Verify permissions are accepted by cookie
      if (Array.isArray(permissions)) {
        _permissions = permissions;
      }

      for (let i = 0, ii = _permissions.length; i < ii; i++) {
        // Allow wildcards for permissions so we can accept any view
        if (this.security.permissions.hasOwnProperty(_permissions[i]) || _permissions[i] === '*') {
          allow = true;
          break;
        }
      }
    } else {
      // Allow anything that is not security enhanced
      allow = true;
    }

    return allow;
  }

  /**
   * Validate permissions are allowed
   * Accepts an Array of Strings or a String
   */
  checkFeatures(features: TODO): boolean {
    let _features: TODO[] = [];
    let allow = false;

    if (this.securityEnhanced) {
      // Verify features are accepted by cookie
      if (Array.isArray(features)) {
        _features = features;
      } else {
        allow = true;
      }

      for (let i = 0, ii = _features.length; i < ii; i++) {
        // Allow wildcards for features so we can accept any view
        if (this.security.features.indexOf(_features[i]) > -1 || _features[i] === '*') {
          allow = true;
          break;
        }
      }
    } else {
      // Allow anything that is not security enhanced
      allow = true;
    }

    return allow;
  }

  /**
   * Remove any items with improper roles
   */
  filterByEngine() {
    for (const item in this.items) {
      if (this.items.hasOwnProperty(item)) {
        this.items[item] = this.filterItems(this.items[item]);
      }
    }
  }

  filterItems(items) {
    if (Array.isArray(items)) {
      const filtered: TODO[] = [];

      for (let i = 0, ii = items.length; i < ii; i++) {
        // Set local variable to current item
        const currentItem = items[i];

        // feature required for this item is not supported
        if (!this.checkFeatures(currentItem.features)) {
          continue;
        }

        if (this.security.engine === 'permissions') {
          if (this.checkPermissions(currentItem.permissions)) {
            filtered.push(currentItem);
          } else {
          }
        } else if (this.security.engine === 'roles') {
          if (this.checkRoles(currentItem.roles)) {
            filtered.push(currentItem);
          } else {
          }
        }

        if (currentItem.hasOwnProperty('items') && Array.isArray(currentItem.items)) {
          currentItem.items = this.filterItems(currentItem.items);
        }
      }
      items = filtered;
    }

    return items;
  }
}

export const NavigationServiceModule = angular
  .module('acme.common.service.navigation', ['acme.common.service.auth'])
  .run([
    function run() {
      // noop
    },
  ])
  .service('NavigationService', NavigationService).name;
